import * as React from 'react';
import classNames from 'classnames';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
    SENTENCE_FIRST_LABELS_MARGIN,
    SENTENCE_FONT_SIZE,
} from 'routes/Explore/components/Sentence/components/SentenceSection';

type Props = {
    className?: string;
    isSelected: boolean;
    hideDelete?: boolean;
    onClick: FlowAnyFunction;
    children: React.ReactNode[] | React.ReactNode;
    removeChunk: FlowAnyFunction;
    displayIndex: number;
    hide?: boolean;
};

const StySentenceChunk = styled(Box, {
    name: 'StySentenceChunk',
    shouldForwardProp: (prop) => !['isSelected', 'hide'].includes(prop),
})<{ isSelected: Props['isSelected']; hide: Props['hide'] }>(({
    isSelected,
    hide,
    theme,
}) => {
    const firstLabelStyle = {
        width: SENTENCE_FIRST_LABELS_MARGIN,
        textAlign: 'right',
        flexShrink: 0,
        marginRight: '1rem',
    };
    /*
     * rules to improve alignment between elements of a same phrase. If you change the font-size,
     * you should fiddle with these values to ensure alignment
     */
    const elementsAlignementRules = {
        '& .MuiInputBase-input': {
            lineHeight: '1.5em',
            height: '1.5em',
        },
        '& .MuiInputBase-root': {
            fontSize: SENTENCE_FONT_SIZE,
            height: '2rem',
        },
        '& .StyDropdownTrigger-root': {
            alignItems: 'flex-end',
        },
        // it improves the vertical alignement of multiselectors with badges
        '& .MuiInputBase-adornedStart input': {
            paddingBottom: '0.25em',
        },
    };
    return {
        ...elementsAlignementRules,
        display: hide ? 'none' : 'flex',
        padding: theme.spacing(0.5),
        flexDirection: 'row',
        alignItems: 'flex-start',
        position: 'relative',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
        },

        borderRadius: '0.3rem',
        backgroundColor: isSelected
            ? theme.palette.primary.background
            : 'inherit',

        '& p': {
            marginRight: '6px',
            fontSize: SENTENCE_FONT_SIZE,
            whiteSpace: 'nowrap',
            lineHeight: '2rem',
        },
        // otherwise some linker words can be too close from the previous element
        '& p:not(.sentence-chunk-first-label)': {
            marginLeft: '6px',
        },
        '& > div > div': {
            marginTop: 0,
        },
        [theme.breakpoints.up('lg')]: {
            // without the ">" selector, the style would be also applied to the "de" keyword too, which is nested under a different div.
            '& > .sentence-chunk-first-label': firstLabelStyle,
            // for filters and dimensions, the label is nested under a StyDropdown div, so previous selector doesn't work.
            '& .tree-dropdown:first-of-type .sentence-chunk-first-label':
                firstLabelStyle,
        },
    };
});

const StySentenceChunkIcon = styled(IconButton, {
    name: 'StySentenceChunkIcon',
})({
    position: 'absolute',
    width: '30px',
    height: '30px',
    padding: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    right: '-35px',
    '& svg': {
        color: '#666',
        fontSize: '18px',
    },
});

const SentenceChunk = (props: Props) => {
    const {
        className,
        hideDelete = false,
        hide = false,
        onClick = () => {},
    } = props;
    const { t } = useTranslation();

    const handleOnClick = () => onClick(props.displayIndex);
    const removeChunk = () => props.removeChunk(props.displayIndex);

    const containerClass = classNames([
        className,
        'sentence-chunk',
        `e2e-index-${props.displayIndex}`,
    ]);

    return (
        <StySentenceChunk
            data-testid={`chunk-index-${props.displayIndex}`}
            className={containerClass}
            onClick={handleOnClick}
            isSelected={props.isSelected}
            role="button"
            // @ts-expect-error [TS migration] Was not detected by flow
            tabIndex="0"
            hide={hide}
        >
            {props.children}
            {!hideDelete && (
                <Tooltip title={t('common.delete')} placement="top">
                    <StySentenceChunkIcon onClick={removeChunk} size="large">
                        <CloseIcon />
                    </StySentenceChunkIcon>
                </Tooltip>
            )}
        </StySentenceChunk>
    );
};

export default SentenceChunk;
